import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service_type: '',
    message: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.service_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', service_type: '', message: '', consent: false });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <main className="pt-20" data-testid="contact-page">
      {/* Page Header */}
      <section className="page-header py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              Contact Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-950 font-serif mb-6">
              Get Your Free Estimate
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed">
              Ready to transform your floors? Contact our team today for a free, no-obligation consultation and estimate.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 section-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-amber-950 font-serif mb-8">
                Contact Information
              </h2>

              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-7 h-7 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-950 text-lg mb-1">Phone</h4>
                    <a href="tel:+18888791244" className="text-amber-600 hover:text-amber-700 font-medium text-lg">
                      +1-888-879-1244
                    </a>
                    <p className="text-stone-500 text-sm mt-1">Call us for immediate assistance</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-950 text-lg mb-1">Email</h4>
                    <a href="mailto:support@buyfusion.store" className="text-amber-600 hover:text-amber-700">
                      support@buyfusion.store
                    </a>
                    <p className="text-stone-500 text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-stone-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-950 text-lg mb-1">Address</h4>
                    <p className="text-stone-600">
                      88 Sunset Boulevard<br />
                      Los Angeles, CA 800134
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-950 text-lg mb-1">Business Hours</h4>
                    <div className="text-stone-600">
                      <p>Mon-Fri: 8:00 AM - 6:00 PM</p>
                      <p>Sat: 9:00 AM - 4:00 PM</p>
                      <p>Sun: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Call Button */}
              <a href="tel:+18888791244" className="md:hidden block">
                <Button
                  className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="contact-page-call-btn"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call Now: +1-888-879-1244
                </Button>
              </a>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-stone-100">
              <h3 className="text-2xl font-semibold text-amber-950 font-serif mb-6">Request Free Estimate</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Name *</label>
                  <Input
                    type="text"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input"
                    data-testid="contact-page-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Phone Number *</label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input"
                    data-testid="contact-page-phone-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input"
                    data-testid="contact-page-email-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Type of Flooring Service *</label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                  >
                    <SelectTrigger className="form-input" data-testid="contact-page-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hardwood">Hardwood Flooring</SelectItem>
                      <SelectItem value="vinyl">Vinyl Flooring</SelectItem>
                      <SelectItem value="laminate">Laminate Flooring</SelectItem>
                      <SelectItem value="tile">Tile Flooring</SelectItem>
                      <SelectItem value="carpet">Carpet Installation</SelectItem>
                      <SelectItem value="repair">Floor Repair</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Message</label>
                  <Textarea
                    placeholder="Tell us about your project..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="form-input min-h-[120px] py-3"
                    data-testid="contact-page-message-input"
                  />
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent-page"
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                    data-testid="contact-page-consent-checkbox"
                  />
                  <label htmlFor="consent-page" className="text-sm text-stone-600">
                    I agree to receive messages and communications from Buy Fusion regarding my inquiry.
                  </label>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="contact-page-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Estimate'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 section-stone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-amber-950 font-serif mb-4">
              Visit Our Showroom
            </h2>
            <p className="text-stone-600">
              Come see our flooring samples in person at our Los Angeles location.
            </p>
          </div>
          <div className="bg-stone-200 rounded-2xl h-80 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-stone-400 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">88 Sunset Boulevard, Los Angeles, CA 800134</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
